import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from "@angular/router";
import { forkJoin, Observable, of, Subject } from "rxjs";
import { catchError, finalize, switchMap, takeUntil, tap } from "rxjs/operators";

// Services / Subjects
import { SegmentSubjectService } from "../../../subject/segment.subject";
import { FeatureService } from "../../../services/feature.service";
import { PropertyService } from "../../../services/property.service";
import { PropertySubject } from "../../../subject/property.subject";
import { ValueService } from "../../../services/value.service";
import { PropertyInitialSubject } from "../../../subject/property.initial.subject";
import { FlowModeSubject } from "../../../subject/flow.mode.subject";
import { FlowService } from "../../../services/flow.service";
import { FlowStateService } from "../../../services/flow.state.service";

// DTOs / Enums
import { FeatureGroupDto } from "../../../dto/feature.group.dto";
import { ValueDto } from "../../../dto/primary.values.information.dto";
import { PropertyInitialResponseDto } from "../../../dto/property.initial.response.dto";
import { FlowMode } from "../../../dto/enum/flow.mode.enum";
import { RecordStageEnum } from "../../../dto/enum/record.stage.enum";

const STEP_KEY = 'details-primary';
const PRICE_GROUP_CODE = 'PRICE';
const PRICE_FEATURE_PREFIX = 'PRICE_';

@Component({
    selector: 'app-property-details-primary',
    templateUrl: './property-details-primary.component.html',
    styleUrls: ['./property-details-primary.component.scss']
})
export class PropertyDetailsPrimaryComponent implements OnInit, OnDestroy {

    // --------- Flujo / navegación -------------
    currentMode: FlowMode = FlowMode.CREATE;
    previousUrl = '';
    nextUrl = '';

    // --------- Propiedad / backend -------------
    propertyInitial: PropertyInitialResponseDto | null = null;
    propertyId: number | undefined;
    segmentLabel: string | undefined = '';

    // --------- UI / estado -------------
    loading = true;
    private destroy$ = new Subject<void>();

    // --------- Datos -------------
    valuePrimaryList: FeatureGroupDto[] = [];
    formData: { [key: string]: any } = {}; // valores por featureCode
    private valueIdByFeatureCode: { [key: string]: number } = {};

    constructor(
        private router: Router,
        private segmentSubject: SegmentSubjectService,
        private featureService: FeatureService,
        private propertySubject: PropertySubject,
        private valueService: ValueService,
        private propertyService: PropertyService,
        private propertyInitialSubject: PropertyInitialSubject,
        private flowModeSubject: FlowModeSubject,
        private flowService: FlowService,
        private flowState: FlowStateService
    ) {}

    // -----------------------------------------------------------
    // Ciclo de vida
    // -----------------------------------------------------------
    ngOnInit(): void {
        // 1) Modo y rutas
        const mode = this.flowModeSubject.getCurrentMode();
        if (!mode) { this.router.navigate(['/list-pending']); return; }
        this.currentMode = mode;
        this.previousUrl = this.flowService.getPreviousStep(STEP_KEY);
        this.nextUrl = this.flowService.getNextStep(STEP_KEY);

        // 2) Paso actual
        this.flowState.setCurrentStep(STEP_KEY);

        // 3) Propiedad y stage
        this.propertyInitial = this.propertyInitialSubject.getCurrentProperty();
        if (!this.propertyInitial?.propertyId) {
            this.router.navigate(['/list-pending']);
            return;
        }
        this.propertyId = this.propertyInitial.propertyId;
        this.segmentLabel = this.resolveSegmentLabel();

        if (this.propertyInitial.recordStage) {
            this.flowState.setRecordStage(this.propertyInitial.recordStage as RecordStageEnum);
        }
        this.flowState.setPropertyId(this.propertyId);

        // 4) Cargar features y aplicar cache (si existe)
        this.loadValuePrimary();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // -----------------------------------------------------------
    // Carga de features
    // -----------------------------------------------------------
    private loadValuePrimary(): void {
        if (!this.propertyId) return;

        this.loading = true;
        this.featureService.getFeaturePrimaryListByPropertyId(this.propertyId).pipe(
            tap((response) => {
                this.valuePrimaryList = this.filterPrimaryFeatureGroups(response || []);
                this.initializeFormData();
                this.applyCache(); // sobrescribe con cache local (si existe)
            }),
            finalize(() => (this.loading = false)),
            catchError((error) => {
                console.error('Error al cargar características:', error);
                alert('Error al cargar las características. Por favor, intente nuevamente.');
                return of([]);
            })
        ).subscribe();
    }

    private initializeFormData(): void {
        this.formData = {};
        this.valueIdByFeatureCode = {};
        this.valuePrimaryList.forEach((group) => {
            group.featureList.forEach((feature: ValueDto) => {
                if (feature.featureCode.includes('TOTAL')) return;
                if (feature.valueId) {
                    this.valueIdByFeatureCode[feature.featureCode] = feature.valueId;
                }

                switch (feature.ctInputType) {
                    case 'WHOLE_NUMERIC_INPUT':
                        this.formData[feature.featureCode] = feature.wholeNumber ?? 0;
                        break;
                    case 'DECIMAL_NUMERIC_INPUT':
                        this.formData[feature.featureCode] = feature.decimalNumber ?? 0.0;
                        break;
                    case 'BOOLEAN_INPUT':
                        this.formData[feature.featureCode] = feature.value === 'true' || feature.value === true;
                        break;
                    case 'TEXT_INPUT':
                    default:
                        this.formData[feature.featureCode] = feature.value ?? '';
                        break;
                }
            });
        });

        // cache inicial
        this.persistToFlowState();
    }

    // -----------------------------------------------------------
    // Cache local (FlowState)
    // -----------------------------------------------------------
    private persistToFlowState(): void {
        this.flowState.setStepData(STEP_KEY, {
            formData: { ...this.formData }
        });
    }

    private applyCache(): void {
        const cached = this.flowState.getStepData(STEP_KEY);
        if (!cached?.formData) return;

        // Solo sobrescribe los featureCodes que existen en este paso
        Object.keys(cached.formData).forEach(code => {
            if (code in this.formData) {
                this.formData[code] = cached.formData[code];
            }
        });
    }

    // -----------------------------------------------------------
    // Guardar (siempre guardar antes de navegar)
    // -----------------------------------------------------------
    private buildPayload(): any[] {
        // backend espera: [{ featureCode, value }]
        return Object.keys(this.formData).map((featureCode) => ({
            featureCode,
            value: String(this.formData[featureCode] ?? '')
        }));
    }

    private filterPrimaryFeatureGroups(groups: FeatureGroupDto[]): FeatureGroupDto[] {
        return (groups || [])
            .filter(group => group.groupCode !== PRICE_GROUP_CODE)
            .map(group => ({
                ...group,
                featureList: (group.featureList || []).filter(feature => !this.isPriceFeature(feature))
            }))
            .filter(group => group.featureList.length > 0);
    }

    private isPriceFeature(feature: ValueDto): boolean {
        return feature?.featureCode?.startsWith(PRICE_FEATURE_PREFIX);
    }

    private resolveSegmentLabel(): string {
        return this.propertyInitial?.segmentLabel
            || this.segmentSubject.getSelectedSegment()?.segmentLabel
            || this.propertySubject.getPropertyName()
            || 'propiedad';
    }

    private saveFeatureValues(): Observable<any> {
        if (!this.propertyId) {
            return of(null);
        }
        const payload = this.buildPayload();
        this.persistToFlowState();

        const newValues = payload.filter(item => !this.valueIdByFeatureCode[item.featureCode]);
        const updateRequests = payload
            .filter(item => !!this.valueIdByFeatureCode[item.featureCode])
            .map(item => this.valueService.updateFeatureValue(this.valueIdByFeatureCode[item.featureCode], item.value));

        const requests: Observable<any>[] = [
            ...(newValues.length ? [this.valueService.registerFeatureValues(this.propertyId, newValues)] : []),
            ...updateRequests
        ];

        const request$: Observable<any> = requests.length ? forkJoin(requests) : of(null);

        return request$.pipe(
            tap(() => {
                // nos aseguramos de reflejar stage localmente (el backend lo actualizará en nextStage)
                this.flowState.setRecordStage(RecordStageEnum.FEATURES_1);
            }),
            catchError((err) => {
                console.error('Error al registrar características:', err);
                alert('Error al registrar las características. Por favor, intente nuevamente.');
                return of(null);
            })
        );
    }

    // Política para avanzar etapa:
    // - CREATE → avanza
    // - EDIT → no avanza
    // - RESUME → avanza sólo si estás en un paso posterior al recordStage
    private shouldAdvanceStage(): boolean {
        if (this.currentMode === FlowMode.EDIT) return false;
        if (this.currentMode === FlowMode.CREATE) return true;

        const stage = this.flowState.recordStage;
        if (!stage) return false;

        const stageStep = this.flowState.getStepByRecordStage(stage);
        const currentIdx = this.flowService.getStepIndex(STEP_KEY);
        const stageIdx = this.flowService.getStepIndex(stageStep);

        if (currentIdx === -1 || stageIdx === -1) return false;
        return currentIdx > stageIdx;
    }

    // -----------------------------------------------------------
    // Navegación (botonera)
    // -----------------------------------------------------------
    handleNextStep(): void {
        // Validación simple: no dejar valores vacíos (puedes ajustarla)
        if (!this.isFormValid()) {
            alert('Por favor, completa los campos requeridos.');
            return;
        }

        this.saveFeatureValues().pipe(
            switchMap(() => {
                if (this.shouldAdvanceStage()) {
                    const body = { id: this.propertyId };
                    return this.propertyService.nextStageProperty(body).pipe(
                        catchError((err) => { console.error('nextStage error', err); return of(null); })
                    );
                }
                return of(null);
            })
        ).subscribe(() => {
            this.router.navigate([this.nextUrl]); // '/details-secondary'
        });
    }

    handleSaveAndExit(): void {
        // Guardar en backend pero NO avanzar etapa
        this.saveFeatureValues().subscribe(() => {
            this.router.navigate(['/list-pending']); // o dashboard
        });
    }

    handlePreviousStep(): void {
        this.persistToFlowState();
        this.router.navigate([this.previousUrl]);
    }

    // -----------------------------------------------------------
    // Validaciones / helpers de UI
    // -----------------------------------------------------------
    private isFormValid(): boolean {
        // Ajusta según reglas específicas. Aquí evitamos null/undefined/''
        return Object.values(this.formData).every((v) => v !== null && v !== undefined && v !== '');
    }

    computeGroupTotal(group: FeatureGroupDto): number {
        if (!group?.featureList?.length) return 0;
        return group.featureList
            .filter(f => f.ctInputType === 'WHOLE_NUMERIC_INPUT' && !f.featureCode.includes('TOTAL'))
            .map(f => Number(this.formData[f.featureCode] ?? 0))
            .reduce((a, b) => a + (isNaN(b) ? 0 : b), 0);
    }

    sanitizeWhole(code: string): void {
        const v = Number(this.formData[code]);
        this.formData[code] = Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0;
        this.persistToFlowState();
    }

    sanitizeDecimal(code: string): void {
        const v = Number(this.formData[code]);
        this.formData[code] = Number.isFinite(v) && v >= 0 ? +v : 0;
        this.persistToFlowState();
    }

    onValueChanged(code: string): void {
        // texto / boolean
        this.persistToFlowState();
    }

    incrementFeature(featureCode: string): void {
        const v = Number(this.formData[featureCode] ?? 0);
        this.formData[featureCode] = (isNaN(v) ? 0 : v) + 1;
        this.persistToFlowState();
    }

    decrementFeature(featureCode: string): void {
        const v = Number(this.formData[featureCode] ?? 0);
        this.formData[featureCode] = Math.max((isNaN(v) ? 0 : v) - 1, 0);
        this.persistToFlowState();
    }

    trackByGroup = (_: number, g: any) => g?.groupId ?? g?.groupLabel;
    trackByFeature = (_: number, f: any) => f?.featureCode;
}
